// src/services/investmentService.js - SECURED
import { API_BASE_URL } from '../utils/constants';


const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

const handleResponse = async (response) => {
  const responseText = await response.text();
  console.log('Raw response:', responseText);
  console.log('Response status:', response.status);
  
  if (!response.ok) {
    // Use secure response handler for errors
    try {
      const errorData = JSON.parse(responseText);
      throw new Error(errorData.error || errorData.detail || 'Request failed');
    } catch (parseError) {
      // If already an Error, rethrow it
      if (parseError instanceof Error && parseError.message !== 'Request failed') {
        throw parseError;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }
  
  // For successful responses, try to parse as JSON
  if (!responseText) {
    return { success: true };
  }
  
  try {
    const data = JSON.parse(responseText);
    console.log('Parsed response data:', data);
    return data;
  } catch {
    console.log('Response is not JSON, returning as text');
    return { success: true, message: responseText };
  }
};

export const investmentService = {
  // Submit investment questionnaire
  async submitQuestionnaire(questionnaireData) {
    try {
      const response = await fetch(`${API_BASE_URL}/investment/questionnaire/submit/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(questionnaireData),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      throw error;
    }
  },

  // Get user's questionnaires
  async getUserQuestionnaires(stockId = null) {
    try {
      const url = stockId 
        ? `${API_BASE_URL}/investment/questionnaire/list/?stock_id=${stockId}`
        : `${API_BASE_URL}/investment/questionnaire/list/`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
      throw error;
    }
  },

  // Create investment after questionnaire
  async createInvestment(investmentData) {
    try {
      const response = await fetch(`${API_BASE_URL}/investment/create/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(investmentData),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error creating investment:', error);
      throw error;
    }
  },

  // Get user's investments
  async getUserInvestments(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.is_locked !== undefined) params.append('is_locked', filters.is_locked);
      
      const url = `${API_BASE_URL}/investment/list/${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching investments:', error);
      throw error;
    }
  },

  // Get specific investment detail
  async getInvestmentDetail(investmentId) {
    try {
      const response = await fetch(`${API_BASE_URL}/investment/${investmentId}/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching investment detail:', error);
      throw error;
    }
  },

  // Get investment summary
  async getInvestmentSummary() {
    try {
      const response = await fetch(`${API_BASE_URL}/investment/summary/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching investment summary:', error);
      throw error;
    }
  },

  // Process withdrawal from investment
  async processWithdrawal(withdrawalData) {
    try {
      const requestData = {
        investment_id: withdrawalData.investment_id,
        withdrawal_type: withdrawalData.withdrawal_type
      };

      if (withdrawalData.withdrawal_type === 'PARTIAL' && withdrawalData.shares_to_sell) {
        requestData.shares_to_sell = withdrawalData.shares_to_sell;
      }

      console.log('Sending withdrawal request:', requestData);

      const response = await fetch(`${API_BASE_URL}/investment/withdraw/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(requestData),
      });
      
      const result = await handleResponse(response);
      console.log('Processed withdrawal result:', result);
      
      if (result && (result.success === true || result.message)) {
        return result;
      } else if (result && result.error) {
        throw new Error(result.error);
      } else {
        return { success: true, ...result };
      }
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      throw error;
    }
  },

  // Get withdrawal history
  async getWithdrawalHistory(investmentId = null) {
    try {
      const url = investmentId 
        ? `${API_BASE_URL}/investment/withdrawals/?investment_id=${investmentId}`
        : `${API_BASE_URL}/investment/withdrawals/`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
      throw error;
    }
  },

  // Get investment notifications
  async getNotifications(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.is_read !== undefined) params.append('is_read', filters.is_read);
      if (filters.type) params.append('type', filters.type);
      
      const url = `${API_BASE_URL}/investment/notifications/${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  // Mark notification as read
  async markNotificationRead(notificationId) {
    try {
      const response = await fetch(`${API_BASE_URL}/investment/notifications/${notificationId}/read/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark all notifications as read
  async markAllNotificationsRead() {
    try {
      const response = await fetch(`${API_BASE_URL}/investment/notifications/read-all/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }
};